import React, { useState, useEffect } from 'react';
import { Layout, Menu, Dropdown, Button, Modal } from 'antd';
import MDEditor from '@uiw/react-md-editor';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';

import tncContent from '@/utils/tnc.md?raw';
import privacyContent from '@/utils/privacry.md?raw';

import CustomTooltip from '@/components/TooltipCustom';
import SimpleButton from '@/components/ButtonSimple';

import { getUser, useGetProfileQuery, useGetCarriersQuery } from '../services/service';
import { clearAuth } from '@/utils/authSession';
import { usePagePreloader } from '@/hooks/useLazyLoad';

import { IconLogout, IconMenuFold, IconMenuUnfold, IconGlobal, IconHome, IconUser, IconInfoCircle } from '@/utils/icons';
import { logHelper } from '@/utils';
import { LOGO_AND_NAME_IMG, USER_PROFILE_IMG } from '@/utils/image';

const { Header, Sider, Content } = Layout;

const TAG: string = 'MainLayout: ';
const MainLayout: React.FC = () => {

  const navigate = useNavigate();
  const location = useLocation();
  const { preloadPage } = usePagePreloader();
  const [collapsed, setCollapsed] = useState(true);

  const userData = getUser();

  useGetCarriersQuery();

  const [modalOpen, setModalOpen] = useState(false);
  const [modalContent, setModalContent] = useState<{ title: string; body: React.ReactNode }>({ title: '', body: null });

  const handleOpenModal = (type: 'terms' | 'privacy' | 'help') => {
    if (type === 'terms') {
      setModalContent({
        title: 'Terms and Conditions',
        body: (
          <div data-color-mode="light">
            <MDEditor.Markdown source={tncContent} />
          </div>
        ),
      });
    } else if (type === 'privacy') {
      setModalContent({
        title: 'Privacy Policy',
        body: (
          <div data-color-mode="light">
            <MDEditor.Markdown source={privacyContent} />
          </div>
        ),
      });
    } else {
      setModalContent({
        title: 'Help',
        body: (
          <p className="text-gray-700 leading-relaxed">
            If you are having issues using our portal it can sometimes be due to your company's internal firewall. Try logging in from a different WiFi and/or making sure you don't have a VPN on. If the problem persists please contact{' '}
            <a href="mailto:Jamie@siteeventlogistics.com" className="text-[#3764DC] hover:underline">
              Jamie@siteeventlogistics.com
            </a>
          </p>
        ),
      });
    }
    setModalOpen(true);
  };

  useEffect(() => {
    if (!userData?.company_name || !userData?.vat_number) {
      if (location.pathname !== '/settings') {
        navigate('/settings');
      }
    }
  }, [userData, location.pathname, navigate]);


  const menuItems = [
    {
      key: '/',
      icon: <IconHome />,
      label: 'Enquiries',
      disabled: !userData?.company_name || !userData?.vat_number,
    },
    {
      key: '/tracking',
      icon: <IconGlobal />,
      label: 'Tracking',
      disabled: !userData?.company_name || !userData?.vat_number,
    },
    {
      key: '/settings',
      icon: <IconUser />,
      label: 'Profile',
    },
  ];

  const handleMenuClick = ({ key }: { key: string }) => {
    // Preload the target page before navigation
    const pageName = key === '/' ? 'home' : key.slice(1); // Remove leading slash
    preloadPage(pageName);
    navigate(key);
    // Auto-close sidebar after navigation (for mobile/tablet)
    setCollapsed(true);
  };

  const handleLogout = () => {
    clearAuth();
  };

  const {
    data: profileData,
  } = useGetProfileQuery();

  const userMenuItems = [
    {
      key: 'profile',
      label: 'Profile',
      icon: <IconUser />,
      onClick: () => {
        preloadPage('settings');
        navigate('/settings');
      },
    },
    {
      key: 'logout',
      label: 'Logout',
      icon: <IconLogout />,
      onClick: handleLogout,
    },
  ];

  // logHelper(TAG, ' profileData ', profileData);

  return (
    <Layout className="min-h-screen !font-montserrat">

      {/* Overlay for mobile/tablet: click to close sidebar */}
      {!collapsed && (
        <div
          className="fixed inset-0 bg-black bg-opacity-30 z-30 lg:hidden"
          onClick={() => setCollapsed(true)}
        />
      )}

      <Sider
        trigger={null}
        collapsible
        collapsed={collapsed}
        breakpoint="lg"
        collapsedWidth="0"
        onBreakpoint={(broken) => {
          if (broken) {
            setCollapsed(true);
          }
        }}
        className={
          `bg-white z-40 transition-transform duration-300 lg:static
          fixed lg:relative top-0 left-0 h-screen lg:h-auto
          ${collapsed ? '-translate-x-full lg:translate-x-0' : 'translate-x-0'}
          w-[260px] lg:w-auto shadow-lg lg:shadow-none`}
        style={{
          width: collapsed ? 0 : 260,
        }}
      >
        {/* Mobile Sider header row: SITE left, close btn right */}
        <div className="flex items-center justify-between px-4 py-3 lg:px-6 lg:py-4 sm:flex md:flex">
          <span className="text-xl font-bold">SITE</span>
          <Button
            type="text"
            icon={<IconMenuFold />}
            onClick={() => setCollapsed(true)}
            className="text-xl"
          />
        </div>
        <Menu
          theme="light"
          mode="inline"
          selectedKeys={[location.pathname]}
          items={menuItems}
          onClick={handleMenuClick}
        />
      </Sider>

      <Layout className="!bg-[#F0F8FF] flex min-h-screen flex-col">

        <Header className="px-4 bg-white flex items-center relative justify-center md:justify-between !h-[50px] md:!h-[80px] shadow-[0_0_8px_8px_#00000021] flex-col md:flex-row overflow-visible">

          <div className="w-full flex items-center gap-2 md:w-auto lg:absolute lg:left-[1rem] ">
            {/* Mobile menu open button (only show when sidebar is collapsed) */}
            {collapsed && (
              <Button
                type="text"
                icon={<IconMenuUnfold />}
                onClick={() => setCollapsed(false)}
                className="text-base w-16 h-16 lg:hidden"
              />
            )}

            <img
              src={LOGO_AND_NAME_IMG}
              alt="logo"
              className="h-auto w-[160px] xs:w-[220px] sm:w-[160px] md:w-[300px] min-w-0"
              loading="lazy"
            />
          </div>

          <div className="gap-2 hidden lg:flex lg:absolute lg:left-1/2 lg:-translate-x-1/2">

            <SimpleButton
              onClick={() => { navigate('/'); }}
              className={`  !text-[14px] !rounded-md  ${location.pathname.includes('enquiries') || location.pathname === '/' ? '!text-[#ffffff] !bg-[#3764DC]' : ' !text-[#3764DC]  !bg-transparent '} border-[1px] border-solid border-[#3764DC] `}
              disabled={!userData?.company_name || !userData?.vat_number}
            >
              ENQUIRIES
            </SimpleButton>

            <SimpleButton
              onClick={() => { navigate('/tracking'); }}
              className={`  !text-[14px] !rounded-md ${location.pathname === '/tracking' ? '!text-[#ffffff] !bg-[#3764DC]' : '!text-[#3764DC]  !bg-transparent '} border-[1px] border-solid border-[#3764DC] `}
              disabled={!userData?.company_name || !userData?.vat_number}
            >
              TRACKING
            </SimpleButton>

          </div>

          <div className="hidden md:flex items-center gap-2 md:ml-auto shrink-0">
            <Dropdown
              menu={{ items: userMenuItems }}
              placement="bottomRight"
              arrow
            >
              <div className="rounded-[22px] h-[40px] flex items-center bg-white p-1 border-[1px] border-solid border-[#D3D3D3] w-[200px] cursor-pointer shrink-0">

                <div className="w-[35px] h-[35px] flex items-center justify-center overflow-hidden shrink-0">
                  <img
                    src={USER_PROFILE_IMG}
                    alt="logo"
                    className=" w-[35px] h-[35px] rounded-[50%] object-cover object-center "
                    loading="lazy"
                  />
                </div>

                <div className="ml-2 flex-1 h-full flex items-center w-[120px] ">
                  <p className="text-[12px] text-[#000000] uppercase truncate shrink-0 w-full ">
                    <CustomTooltip title={profileData?.data?.data?.company_name || "_"}>
                      {profileData?.data?.data?.company_name || '_'}
                    </CustomTooltip>
                  </p>
                </div>

                <div className="h-full flex items-center justify-center mr-2">
                  <svg className="w-[13px] h-auto " width="16" height="10" viewBox="0 0 16 10" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M1 1.5L8 8.5L15 1.5" stroke="#1A1A1A" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </div>

              </div>
            </Dropdown>
            <div className="flex items-center justify-center">
              <Button
                type="text"
                icon={<IconInfoCircle className="text-[#3764DC] !text-2xl" />}
                onClick={() => handleOpenModal('help')}
                className="!w-10 !h-10 !min-w-[40px] !pt-2 cursor-pointer"
                title="Help"
              />
            </div>
          </div>

          <div className="absolute right-[1rem] md:hidden ">
            <Button
              type="text"
              icon={<IconLogout />}
              onClick={handleLogout}
              className="text-base w-16 h-16 lg:hidden"
            />
          </div>

        </Header>

        <Content className="m-0 min-h-0 flex-1 overflow-y-auto overflow-x-hidden bg-[#F0F8FF] p-3">
          <Outlet />
        </Content>

        <footer className="hidden shrink-0 border-t border-gray-200 bg-[#F0F8FF] py-2 text-center text-[12px] leading-[1.3] text-gray-500 md:block">
          All rights reserved by SITE GLOBAL EVENT LOGISTICS © 2025
          <span className="mx-1">|</span>
          <span
            className="cursor-pointer hover:text-blue-600 hover:underline"
            onClick={() => handleOpenModal('terms')}
          >
            Terms and Conditions
          </span>
          <span className="mx-1">|</span>
          <span
            className="cursor-pointer hover:text-blue-600 hover:underline"
            onClick={() => handleOpenModal('privacy')}
          >
            Privacy Policy
          </span>
        </footer>

        <Modal
          // title={modalContent.title}
          open={modalOpen}
          onCancel={() => setModalOpen(false)}
          width={800}
          centered
          footer={null}
        >
          <div className="p-4">
            {modalContent.body}
          </div>
        </Modal>

      </Layout>
    </Layout>
  );
};

export default MainLayout; 